import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import SocketService from '../../../../packages/shared/socket';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  padding: 40px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 40px;
  border-bottom: 2px solid #333;
  padding-bottom: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 10px 0;
`;

const Subtitle = styled.p`
  color: #888;
  font-size: 1.1rem;
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
`;

const StatNumber = styled.div`
  color: #00ff88;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #888;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SubmissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubmissionCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #00ff88;
    background: rgba(0, 255, 136, 0.05);
  }
`;

const SubmissionHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 12px;
  gap: 20px;
`;

const ParticipantId = styled.span`
  color: #00ff88;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(0, 255, 136, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
`;

const Timestamp = styled.span`
  color: #666;
  font-size: 0.8rem;
  margin-left: auto;
`;

const SubmissionText = styled.div`
  color: #ffffff;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 8px;
`;

const SubmissionMeta = styled.div`
  color: #888;
  font-size: 0.85rem;
  display: flex;
  gap: 15px;
`;

const WeatherScenariosSection = styled.div`
  margin-top: 15px;
  padding: 15px;
  background: rgba(79, 195, 247, 0.1);
  border-left: 3px solid #4fc3f7;
  border-radius: 8px;
`;

const WeatherScenariosTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #4fc3f7;
  font-size: 0.9rem;
  font-weight: 600;
`;

const WeatherScenario = styled.div`
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ScenarioTitle = styled.div`
  color: #ffcc02;
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 3px;
`;

const ScenarioTrigger = styled.div`
  color: #888;
  font-size: 0.8rem;
  font-style: italic;
  margin-bottom: 5px;
`;

const ScenarioDescription = styled.div`
  color: #ddd;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
`;

function CommentsScreen() {
  const [submissions, setSubmissions] = useState([]);
  const [participants, setParticipants] = useState(0);
  const [displays, setDisplays] = useState(0);

  useEffect(() => {
    console.log('CommentsScreen: Setting up listeners');
    console.log('CommentsScreen: Socket connected?', SocketService.socket?.connected);
    
    // Named so the cleanup removes only these from the shared socket (#1427)
    const onSubmission = (submission) => {
      console.log('CommentsScreen: Received submission:', submission);
      setSubmissions(prev => [submission, ...prev]);
    };
    const onParticipantUpdate = (update) => {
      console.log('CommentsScreen: Participant update:', update);
      setParticipants(update.tablets || 0);
      setDisplays(update.displays || 0);
    };
    const onSessionState = (state) => {
      console.log('CommentsScreen: Received session state:', state);
      setParticipants(state.participants || 0);
      setDisplays(state.displays || 0);
      setSubmissions(state.submissions || []);
    };

    let socket = null; // the socket the listeners went on
    let retryTimer = null;

    // Wait for existing connection from App.jsx instead of creating new one
    const checkConnection = () => {
      if (SocketService.socket && SocketService.socket.connected) {
        console.log('CommentsScreen: Using existing socket connection');
        setupListeners();
      } else {
        console.log('CommentsScreen: Waiting for socket connection...');
        retryTimer = setTimeout(checkConnection, 500);
      }
    };

    const setupListeners = () => {
      socket = SocketService.socket;
      socket.on('new-submission', onSubmission);
      socket.on('participant-update', onParticipantUpdate);
      socket.on('session-state', onSessionState);

      // Request current session state
      console.log('CommentsScreen: Requesting session state...');
      socket.emit('get-session-state');
    };

    checkConnection();

    return () => {
      clearTimeout(retryTimer);
      if (socket) {
        socket.off('new-submission', onSubmission);
        socket.off('participant-update', onParticipantUpdate);
        socket.off('session-state', onSessionState);
      }
    };
  }, []);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('fi-FI', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('fi-FI');
  };

  return (
    <Container data-testid="comments-screen">
      <Header>
        <Title>KUUTIO Facilitator</Title>
        <Subtitle>Real-time submissions and session monitoring</Subtitle>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatNumber>{submissions.length}</StatNumber>
          <StatLabel>Total Submissions</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber data-testid="tablet-count">{participants}</StatNumber>
          <StatLabel>Active Tablets</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber data-testid="display-count">{displays}</StatNumber>
          <StatLabel>Connected Displays</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{submissions.filter(s => {
            const now = new Date();
            const subTime = new Date(s.timestamp);
            return (now - subTime) < 60000; // Last minute
          }).length}</StatNumber>
          <StatLabel>Last Minute</StatLabel>
        </StatCard>
      </StatsContainer>

      <SubmissionsList>
        {submissions.length === 0 ? (
          <EmptyState>
            <EmptyIcon>💭</EmptyIcon>
            <div>Waiting for submissions...</div>
            <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
              Participants can join at the tablet interface
            </div>
          </EmptyState>
        ) : (
          submissions.map((submission, index) => (
            <SubmissionCard key={submission.id || index}>
              <SubmissionHeader>
                <ParticipantId>
                  {submission.participantId?.slice(-8) || 'Unknown'}
                </ParticipantId>
                <Timestamp>
                  {formatDate(submission.timestamp)} {formatTime(submission.timestamp)}
                </Timestamp>
              </SubmissionHeader>
              
              <SubmissionText>
                {submission.text || submission.content || JSON.stringify(submission.data)}
              </SubmissionText>
              
              <SubmissionMeta>
                {submission.category && <span>Category: {submission.category}</span>}
                {submission.type && <span>Type: {submission.type}</span>}
                <span>Session: {submission.sessionId || 'default'}</span>
              </SubmissionMeta>
              
              {submission.weatherScenarios && submission.weatherScenarios.length > 0 && (
                <WeatherScenariosSection>
                  <WeatherScenariosTitle>🌤️ Weather-Based Scenarios</WeatherScenariosTitle>
                  {submission.weatherScenarios.map((scenario, idx) => (
                    <WeatherScenario key={idx}>
                      <ScenarioTitle>{scenario.title}</ScenarioTitle>
                      <ScenarioTrigger>{scenario.trigger}</ScenarioTrigger>
                      <ScenarioDescription>{scenario.description}</ScenarioDescription>
                    </WeatherScenario>
                  ))}
                </WeatherScenariosSection>
              )}
            </SubmissionCard>
          ))
        )}
      </SubmissionsList>
    </Container>
  );
}

export default CommentsScreen;